import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  FileInfo,
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PhotoCaptureModalData,
  PhotoCaptureModalResult
} from "../../models";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { DialogService } from "../../services/dialog.service";
import {
  GeneralConfirmationModalComponent
} from "../general-confirmation-modal/general-confirmation-modal.component";
import { PhotoCaptureModalComponent } from "../photo-capture-modal/photo-capture-modal.component";

@Component({
  selector: 'calingo-fnol-drag-n-drop',
  templateUrl: './drag-n-drop.component.html',
  styleUrls: ['./drag-n-drop.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DragNDropComponent {
  @Input() singleFileAllowed: boolean = false;
  @Input() validate: boolean = false;
  /** Upload budget passed on to the camera modal so it can warn before a PDF is even built. */
  @Input() maxFileSizeBytes: number | null = null;

  @Output() onFileChange: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('fileUpload', { static: true }) fileUpload!: ElementRef;

  fileList: FileInfo[] = [];
  isFileDragOver = false;
  isValid = true;

  constructor(private dialog: MatDialog,
              private dialogService: DialogService,
              private translate: TranslateService) {
  }

  get files() {
    return this.fileList;
  }

  set files(files: FileInfo[]) {
    this.fileList = files;
  }

  get filesSize() {
    return this.fileList.reduce((acc, curr) => acc + (curr.size || 0), 0);
  }

  resetFiles() {
    this.fileList = [];
    this.fileUpload.nativeElement.value = '';
  }

  deleteFile(file: FileInfo) {
    this.fileList = this.fileList.filter(f => f !== file);
    this.onFileChange.emit();
  }

  dragEnterHandler(event: any) {
    this.isFileDragOver = true;
    event.preventDefault();
  }

  dragLeaveHandler(event: any) {
    this.isFileDragOver = false;
    event.preventDefault();
  }

  dragOverHandler(event: any) {
    event.preventDefault();
  }

  async dropHandler($event: DragEvent) {
    $event.preventDefault();

    if ($event.dataTransfer?.items) {

      if (this.singleFileAllowed && $event.dataTransfer.items.length > 1) {
        this.showSingleFileOnlyNotice();
        return;
      }

      for (let i = 0; i < $event.dataTransfer.items.length; i++) {
        let item = $event.dataTransfer.items[i];

        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            this.fileList.push(await this.toBase64(file));
            this.onFileChange.emit();
          }
        }
      }
    } else {
      if ($event.dataTransfer?.files) {
        if (this.singleFileAllowed && $event.dataTransfer.files.length > 1) {
          this.showSingleFileOnlyNotice();
          return;
        }

        for (let i = 0; i < $event.dataTransfer?.files?.length; i++) {
          this.fileList.push(await this.toBase64($event.dataTransfer?.files[i]));
          this.onFileChange.emit();
        }
      }
    }
  }

  async fileChangeHandler(event: any) {
    if (event.target.files) {
      if (this.singleFileAllowed && event.target.files.length > 1) {
        this.showSingleFileOnlyNotice();
        return;
      }

      for (let i = 0; i < event.target.files.length; i++) {
        this.fileList.push(await this.toBase64(event.target.files[i]));
        this.onFileChange.emit();
      }
    }
  }

  toBase64 = (file: File): Promise<FileInfo> => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve({
      name: this.removeSpecialCharacters(file.name),
      data64: reader.result as string,
      size: file.size
    });
    reader.onerror = error => reject(error);
  });

  removeSpecialCharacters(filename: string): string {
    // Replace non-ASCII characters and '+' signs with '-'
    return filename.replace(/[+]|[^\x00-\x7F]+/g, '-');
  }

  private showSingleFileOnlyNotice() {
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: this.translate.instant('drag-n-drop.single-file-only'),
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: 'OK',
      } as GeneralConfirmationModalData
    });
  }

  openCamera(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    let dialogRef = this.dialog.open<PhotoCaptureModalComponent, PhotoCaptureModalData, PhotoCaptureModalResult>(
      PhotoCaptureModalComponent, {
        disableClose: true,
        data: { maxSizeBytes: this.maxFileSizeBytes ?? undefined },
      });

    dialogRef.afterClosed().subscribe(async result => {
      if (result?.pdfBlob) {
        // Create a File object from the PDF blob with a meaningful name
        const fileName = `Photos_${new Date().toISOString().slice(0, 10)}.pdf`;
        const pdfFile = new File([result.pdfBlob], fileName, { type: 'application/pdf' });
        this.fileList.push(await this.toBase64(pdfFile));
        this.onFileChange.emit();
      }
    });
  }
}
